/**
 * Settings page for tournament configuration.
 *
 * Manages tournament features and integrations:
 * - generation_allowed: Whether tournament generation is enabled
 * - printing_allowed: Whether print views are accessible
 * - Telegram bot configuration for Tulosfeed notifications
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { configApi } from '@/api/client';

// Telegram notification levels
type TelegramLevel = 'off' | 'finals' | 'rounds' | 'blocks' | 'games' | 'all';

interface Settings {
  generation_allowed: boolean;
  printing_allowed: boolean;
  submission_allowed: boolean;
  tournament_date: string;
  telegram_bot_token: string;
  telegram_channel_id: string;
  telegram_level: TelegramLevel;
}

// Default settings - no hardcoded secrets
const DEFAULT_SETTINGS: Settings = {
  generation_allowed: false,
  printing_allowed: false,
  submission_allowed: false,
  tournament_date: '',
  telegram_bot_token: '',
  telegram_channel_id: '',
  telegram_level: 'games',
};

const TELEGRAM_LEVELS: { value: TelegramLevel; label: string; description: string }[] = [
  { value: 'off', label: 'Pois käytöstä', description: 'Ei ilmoituksia' },
  { value: 'finals', label: 'Vain finaalit', description: 'Finaali ja pronssipeli' },
  { value: 'rounds', label: 'Kierroksen päättymiset', description: 'Kun kierros päättyy' },
  { value: 'blocks', label: 'Lohkon päättymiset', description: 'Kun lohko päättyy' },
  { value: 'games', label: 'Kaikki pelit', description: 'Jokainen pelin päättyminen' },
  { value: 'all', label: 'Kaikki tapahtumat', description: 'Myös aloitukset ja puoliajat' },
];

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [showToken, setShowToken] = useState(false);
  const [editingToken, setEditingToken] = useState(false);
  const [editingChannel, setEditingChannel] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch settings
  const { data: settingsConfig, isLoading } = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: () => configApi.get<Settings>('settings'),
    retry: false,
  });

  // Update local state when data loads
  useEffect(() => {
    if (settingsConfig?.value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSettings({ ...DEFAULT_SETTINGS, ...settingsConfig.value });
    }
  }, [settingsConfig]);

  // Update mutation
  const updateSettings = useMutation({
    mutationFn: (newSettings: Settings) => configApi.update('settings', newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'settings'] });
      setSnackbar({ open: true, message: 'Asetukset tallennettu!', severity: 'success' });
    },
    onError: (err: Error) => {
      setSnackbar({ open: true, message: `Virhe: ${err.message}`, severity: 'error' });
    },
  });

  const handleToggle = (key: keyof Settings) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    updateSettings.mutate(newSettings);
  };

  const handleChange = (key: keyof Settings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSelectChange = (key: keyof Settings, value: string) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    updateSettings.mutate(newSettings);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Asetukset
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Hallitse turnauksen asetuksia ja integraatioita.
      </Typography>

      {/* Tournament Features */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Toiminnot
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.generation_allowed}
                onChange={() => handleToggle('generation_allowed')}
                disabled={updateSettings.isPending}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Turnauksen generointi sallittu</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sallii turnauksen lohkojen ja pelien generoinnin
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.submission_allowed}
                onChange={() => handleToggle('submission_allowed')}
                disabled={updateSettings.isPending}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Tulosten lähettäminen sallittu</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sallii tuomareiden lähettää tuloksia tietokantaan
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.printing_allowed}
                onChange={() => handleToggle('printing_allowed')}
                disabled={updateSettings.isPending}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Tulostus sallittu</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sallii tulostusnäkymien käytön (joukkuelaput, tuomarilomakkeet)
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Turnauspäivä
            </Typography>
            <TextField
              type="date"
              value={settings.tournament_date}
              onChange={(e) => {
                const newSettings = { ...settings, tournament_date: e.target.value };
                setSettings(newSettings);
                updateSettings.mutate(newSettings);
              }}
              disabled={updateSettings.isPending}
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText="Turnauspäivä tuomarivuorojen oletuspäiväksi"
              sx={{ minWidth: 200 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Telegram Configuration */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Telegram (Tulosfeed)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Automaattiset ilmoitukset pelituloksista Telegram-kanavalle.
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Bot Token - with inline edit */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Bot Token
            </Typography>
            {!editingToken ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', flex: 1 }}>
                  {settings.telegram_bot_token ? '••••••••' + settings.telegram_bot_token.slice(-8) : '(ei asetettu)'}
                </Typography>
                <IconButton size="small" onClick={() => setEditingToken(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  value={settings.telegram_bot_token}
                  onChange={(e) => handleChange('telegram_bot_token', e.target.value)}
                  disabled={updateSettings.isPending}
                  fullWidth
                  size="small"
                  type={showToken ? 'text' : 'password'}
                  placeholder="524200836:AAE..."
                  helperText="Bot token saadaan @BotFather -botilta"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowToken(!showToken)}
                          edge="end"
                          size="small"
                        >
                          {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    updateSettings.mutate(settings);
                    setEditingToken(false);
                  }}
                  disabled={updateSettings.isPending}
                  title="Tallenna"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setEditingToken(false)}
                  title="Peruuta"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Channel ID - with inline edit */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Kanavan ID
            </Typography>
            {!editingChannel ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', flex: 1 }}>
                  {settings.telegram_channel_id || '(ei asetettu)'}
                </Typography>
                <IconButton size="small" onClick={() => setEditingChannel(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  value={settings.telegram_channel_id}
                  onChange={(e) => handleChange('telegram_channel_id', e.target.value)}
                  disabled={updateSettings.isPending}
                  fullWidth
                  size="small"
                  placeholder="-259977594 tai @tulosfeed"
                  helperText="Kanavan numeerinen ID tai @käyttäjänimi"
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    updateSettings.mutate(settings);
                    setEditingChannel(false);
                  }}
                  disabled={updateSettings.isPending}
                  title="Tallenna"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setEditingChannel(false)}
                  title="Peruuta"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Notification Level - always editable */}
          <FormControl fullWidth size="small">
            <InputLabel>Ilmoitustaso</InputLabel>
            <Select
              value={settings.telegram_level}
              label="Ilmoitustaso"
              onChange={(e) => handleSelectChange('telegram_level', e.target.value)}
              disabled={updateSettings.isPending}
            >
              {TELEGRAM_LEVELS.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  <Box>
                    <Typography variant="body1">{level.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {level.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {settings.telegram_bot_token && settings.telegram_channel_id && settings.telegram_level !== 'off' && (
            <Alert severity="success">
              Telegram-ilmoitukset käytössä tasolla: {TELEGRAM_LEVELS.find(l => l.value === settings.telegram_level)?.label}
            </Alert>
          )}

          {(!settings.telegram_bot_token || !settings.telegram_channel_id) && settings.telegram_level !== 'off' && (
            <Alert severity="warning">
              Anna Bot Token ja Kanavan ID ottaaksesi Telegram-ilmoitukset käyttöön.
            </Alert>
          )}

          {settings.telegram_level === 'off' && (
            <Alert severity="info">
              Telegram-ilmoitukset on poistettu käytöstä.
            </Alert>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
